import React, { FormEvent } from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import { 
  TextArea as KendoTextArea, 
  TextAreaHandle
} from '@progress/kendo-react-inputs';
import PuzzleButton from '../../button/PuzzleButton';
import { classNames } from '@progress/kendo-react-common';
import { Label } from '@progress/kendo-react-labels';
import * as _ from 'lodash';
import Field from '../../../layout/field/Field';
import { PUZZLE_TEXT_AREA_PROPS, PUZZLE_TEXTAREA_STATE } from './Types';
import { 
  ABSTRACT_FIELD_OMITTED_PROPS, 
  FIELD_OMITTED_PROPS
} from '../../../../utility';

/**
 * For the layout and design purposes the entire Kendo NumericTextBox needs to be wrapped with this cmp.
 * Cmp handles all events that occur and flags component state represented with CSS classes, cmp also can be used with
 * additional action btn
 * @author Stevan Stojanovic
 */
class PuzzleTextArea extends PureComponent<PUZZLE_TEXT_AREA_PROPS, PUZZLE_TEXTAREA_STATE> {
  textAreaReference: any;
  textAreaID: any = null;

  state:PUZZLE_TEXTAREA_STATE = {
    maxCharCounter: null
  }

  static defaultProps = {
    size: 'medium',
    screenReaderCharText: 'characters remaining',
    numberOfRowsBeforeScroll: 2
  }

  componentDidMount() {
    const { maxLength, id } = this.props;
    this.textAreaID = id ? id : this.textAreaReference?.element?.current?.id;

    if (maxLength) {
      this.setState({ maxCharCounter: maxLength });
    }
  }

  componentDidUpdate() {
    const maxChar = this.props.maxLength - this.textAreaReference.element.current.value.length;
    this.setState({
      maxCharCounter: maxChar
    });
  }

  /**
   * @param e SyntheticEvent
   * Handler for action button inside textarea for focusing target upon clicking
   * */
  handleSendButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.textAreaReference.focus();
    this.props.withSendButtonConfig?.onClick?.(e);
  }

  /**
   * @param event FormEvent
   * Handles setting max height for the dynamic size of textarea
   * */
  handleTextAreaInput = (event: FormEvent<HTMLTextAreaElement>) => {
    const { autoSize, numberOfRowsBeforeScroll, onInput } = this.props;
    if (autoSize && numberOfRowsBeforeScroll) {
      this.textAreaReference.element.current.style.maxHeight = `${numberOfRowsBeforeScroll * 20 + 8}px`;
    }
    onInput?.(event);
  }

  render() {
    const { maxCharCounter } = this.state;
    const {
      asField,
      autoSize,
      ariaDescribedBy,
      dataCy,
      disabled,
      errorTxt,
      className = '',
      labelClassName = '',
      label,
      value,
      valid,
      resizable,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      hint: _hint = 'textarea',
      withCounter,
      maxLength,
      screenReaderCharText,
      withSendButton,
      withSendButtonConfig
    } = this.props;

    const textAreaOmittedProps = [
      'dataCy',
      'validationType',
      'withCounter',
      'hint',
      'screenReaderCharText',
      'numberOfRowsBeforeScroll',
      'withSendButtonConfig',
      'withSendButton'].concat(FIELD_OMITTED_PROPS, ABSTRACT_FIELD_OMITTED_PROPS);
    const textAreaProps = _.omit(this.props, textAreaOmittedProps);
    const fieldCls = classNames(
      'tp-textarea-wrap',
      {
        [className]: className,
        'tp-resizable': resizable,
        'k-state-empty': !value,
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled,
        'tp-with-dynamic-size': autoSize,
        'tp-with-send-button': withSendButton,
        'tp-with-counter': withCounter
      }
    );
    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };
    const hint = label && _hint ? `${label} ${_hint}` : _hint;
    const _ariaDescribedBy = maxLength && withCounter ? ('tp-textarea-hint tp-textarea-counter ' + ariaDescribedBy) : ariaDescribedBy ? ariaDescribedBy : undefined;
    const textAreaContent = (
      <KendoTextArea
        id={this.textAreaID}
        ref={(textarea: TextAreaHandle) => this.textAreaReference = textarea}
        autoComplete={'off'}
        {...textAreaProps}
        onInput={this.handleTextAreaInput}
        className={'tp-textarea'}
        ariaDescribedBy={_ariaDescribedBy}
        data-cy={dataCy ? dataCy : undefined}
      />);
    const content = (
      <div
        className={fieldCls}
        data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
      >
        {label && <Label
          editorId={this.textAreaID} 
          className={classNames({
            [labelClassName]: labelClassName,
            '!k-display-flex k-justify-content-between': maxLength && withCounter,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          <span>
            {label}
          </span>
          {maxLength && withCounter ? <div
            aria-live={'polite'}
            id={'tp-textarea-char-counter'}
          >
            {maxCharCounter}
            <span className="sr-only">{screenReaderCharText}</span>
          </div> : null}
        </Label>}
        {withSendButton ? <div className={'tp-textarea-button-wrapper'}>
          {textAreaContent}
          <PuzzleButton
            {...withSendButtonConfig}
            style={{
              ...withSendButtonConfig.style,
              zIndex: 1
            }}
            onClick={this.handleSendButtonClick}
          >
            {withSendButtonConfig.name}
          </PuzzleButton>
        </div> : textAreaContent}
        {withValidation && <div
          className={'tp-validation-error-message'}
          data-cy={dataCy ? `${dataCy}-error-message` : undefined}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
        {maxLength && withCounter ? <div
          className="sr-only"
          id={'tp-textarea-hint-message'}
        >{hint}</div> : null }
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }
}

export default PuzzleTextArea;
